// The Inbox list preview of a media message. The bot stores internal markers as a media row's body: "[audio]" (the
// placeholder before processing), "[audio:error]" (transcription / description FAILED — the attachment itself is
// usually fine and plays in the conversation), "[unclear_audio]", or a "[IMAGE]: …" description prefix. None of
// those are for the agent, so a media row previews as its localized kind ("Voice message", "Photo", …), followed by
// the real transcript/caption text when there is one.
//
// Only a row the server says is media (its structured `type`) is relabelled: a customer who TYPES "[audio:error]"
// in a text message still sees exactly that. Delivery failure is a separate fact (the message status), never
// inferred from a marker, and is shown, not hidden.

import type { TFunction } from 'i18next';

export const FAILED_TAG = /^\[(audio|video|image|document):error\]$/;
export const PLACEHOLDER_TAG = /^\[(audio|video|image|document|sticker|voice)\]$/;
export const UNCLEAR_TAG = /^\[unclear_(audio|media)\]$/i;
export const DESCRIPTION_PREFIX = /^\[(VIDEO|IMAGE|IMAGE_PRODUCT_ANALYSIS|DOCUMENT)\]:\s*/;

const MEDIA_LABEL: Record<string, string> = {
  image: 'media.image', document: 'media.document', video: 'media.video', sticker: 'media.sticker',
};

export function mediaPreview(
  body: string | null | undefined,
  type: string | null | undefined,
  { voice = false, failed = false }: { voice?: boolean; failed?: boolean },
  t: TFunction,
): string | null {
  const isAudio = type === 'audio' || type === 'voice';
  const key = isAudio ? (voice || type === 'voice' ? 'media.audio' : 'media.audio_file') : type ? MEDIA_LABEL[type] : undefined;
  if (!key) return null;
  const b = (body ?? '').trim();
  const internal = !b || FAILED_TAG.test(b) || PLACEHOLDER_TAG.test(b) || UNCLEAR_TAG.test(b);
  const text = internal ? '' : b.replace(DESCRIPTION_PREFIX, '').trim();
  const label = t(key);
  const main = text ? `${label} · ${text}` : label;
  return failed ? `${main} · ${t('chat.status_failed')}` : main;
}
